/**
 * Brix Beverage — New Account Application Terms & Conditions.
 *
 * Rendered on the public /apply form; the applicant must accept before
 * submitting, and the accepted version string is stored on the onboarding
 * request (terms_version + terms_accepted_at). Bump TERMS_VERSION whenever
 * the copy meaningfully changes so acceptances stay attributable.
 *
 * Figures kept consistent with live policy: $35 returned-payment fee,
 * 6-month billing-dispute window, $165 lost-cylinder replacement, 100-lb
 * CO₂ fire-code threshold, card-on-file requirement for rentals.
 */

export const TERMS_VERSION = '2026-09-22';

export interface TermsSection {
  heading: string;
  paragraphs: string[];
}

export const APPLICATION_TERMS: TermsSection[] = [
  {
    heading: '1. Account & Payment Terms',
    paragraphs: [
      'New accounts are payment-on-delivery (card or ACH on file) unless credit terms are applied for and approved in writing by Alameda Point Beverage Group Inc. (“Brix Beverage”). Approved credit terms apply from the invoice date; Brix Beverage may adjust, suspend, or revoke credit terms at any time based on payment history or updated credit information.',
      'When your account is approved you will receive an email to log in and activate your account. At that time you will be asked to enter a payment method. All customers that rent equipment or have equipment or cylinder rentals must keep a valid card on file, which you authorize us to charge for recurring rental fees and, where applicable, open balances in accordance with these terms.',
    ],
  },
  {
    heading: '2. Late & Returned Payments',
    paragraphs: [
      'Past-due balances accrue a finance charge of 1.5% per month (18% per annum) or the maximum rate permitted by law, whichever is less. A $35 returned-payment fee applies to any ACH or check returned by your bank, and the underlying invoice is reinstated as unpaid. Accounts with a returned payment may have ordering paused until the balance is settled.',
      'You agree to pay reasonable costs of collection, including collection-agency charges and attorney’s fees, on any balance referred for collection.',
    ],
  },
  {
    heading: '3. Billing Questions & Disputes',
    paragraphs: [
      'Invoice questions or disputes must be raised within six (6) months of the invoice date. After that period, signed delivery records may no longer be on file and credits cannot be issued.',
    ],
  },
  {
    heading: '4. Deliveries',
    paragraphs: [
      'Delivery days and time windows are scheduled as a best effort and may vary with routing, weather, and traffic. You are responsible for providing safe, lawful access to the delivery location (including gate codes, keys, or escort where needed) during your stated delivery windows. Title and risk of loss for product pass to you upon delivery.',
      'If our driver cannot access the delivery location during the scheduled window, a re-delivery trip may be subject to a delivery fee.',
    ],
  },
  {
    heading: '5. Equipment & Cylinder Rentals',
    paragraphs: [
      'All rented equipment and gas cylinders remain the property of Brix Beverage. A monthly rental (demurrage) fee applies to each cylinder on site and funds DOT-mandated recertification, valve/seal maintenance, and safety upkeep. Rental fees continue to accrue while equipment or cylinders remain at your location, whether or not they are in use; unused items can be scheduled for free pickup at any time.',
      'Lost or unreturned cylinders are billed at the current replacement rate ($165 per cylinder as of this version). Brix drivers periodically count the cylinders on site (a Tank Rental Audit, with photos and a signature); an account whose records and count disagree is audited on its next delivery, and cylinders confirmed missing at an audit are charged at the replacement rate. Storing more than 100 lbs of CO₂ on premises requires a fire-department permit under CFC/IFC Section 5307 — compliance is your responsibility; Brix Beverage offers a flat-fee permitting and safety-alarm package if needed.',
    ],
  },
  {
    heading: '6. Credit Applications',
    paragraphs: [
      'By submitting a credit application you certify the information provided is accurate and complete, and you authorize Brix Beverage to contact the trade references and financial institution listed (and to obtain business credit reports, including D&B) for the purpose of evaluating and maintaining your account.',
    ],
  },
  {
    heading: '7. Sales Tax & Resale',
    paragraphs: [
      'Applicable sales tax is charged unless a valid resale or exemption certificate is on file. You are responsible for keeping your certificate current; expired or invalid certificates result in tax being charged until a valid replacement is received.',
    ],
  },
  {
    heading: '8. Communications',
    paragraphs: [
      'You consent to receive transactional communications about your account (order confirmations, delivery updates, invoices, statements, and payment reminders) at the email addresses you provide. Notification preferences can be managed any time in the customer portal.',
    ],
  },
  {
    heading: '9. General',
    paragraphs: [
      'These terms are governed by the laws of the State of California. They may be updated from time to time; the version in effect when you place an order applies to that order, and material changes will be communicated to the account contacts on file. If any provision is held unenforceable, the remainder stays in effect.',
    ],
  },
];
